'use client'

import React from 'react'

import { Loading } from '../../components/Loading'
import { Modal, ModalProps } from '../../components/Modal'
import { Tabs } from '../../components/Tabs'
import { Arrow } from '../../icons'
import { cn } from '../../utils/cn'
import { getBonusRate } from '../../utils/getBonusRate'
import { Notes } from './Notes'
import { PlanCard } from './PlanCard'
import { PointCard } from './PointCard'
import { PurchaseSummary } from './PurchaseSummary'
import {
  PointPackItem,
  PointPurchaseSelection,
  PointPurchaseTab,
  ShortagePurchaseItem,
  SubscriptionPlanItem,
} from './types'

export { BonusBadge, type BonusBadgeProps } from './BonusBadge'
export { FeeBreakdown, type FeeBreakdownProps } from './FeeBreakdown'
export { PlanCard, type PlanCardProps, type PlanCardState } from './PlanCard'
export { PointCard, type PointCardProps } from './PointCard'
export { PurchaseSummary, type PurchaseSummaryProps } from './PurchaseSummary'
export * from './types'

const DEFAULT_SUBSCRIPTION_SERVICE_URL = 'https://service.gyym.jp/subscription'
const DEFAULT_SUBSCRIPTION_SUPPORT_URL =
  'https://support.gyym.jp/hc/ja/categories/29544675492505'

const tabsId = 'point-purchase-tabs'

// Subscription plans are laid out in a 2-column grid (`grid-cols-2` below)
const SUBSCRIPTION_COLUMNS = 2

const tabs: { label: string; value: PointPurchaseTab }[] = [
  {
    label: '定期購入',
    value: 'subscription',
  },
  {
    label: '都度購入',
    value: 'spot',
  },
]

export interface PointPurchaseModalProps extends Omit<ModalProps, 'title'> {
  /**
   * Renders a spinner in place of the body
   */
  isLoading?: boolean

  /**
   * Whether the 定期購入 / 都度購入 tabs are shown. Only users who can still
   * subscribe see them.
   */
  showTabs?: boolean

  /**
   * Active tab. Ignored while `showTabs` is `false`.
   */
  tab?: PointPurchaseTab

  onTabChange?: (tab: PointPurchaseTab) => void

  /**
   * Subscription plans listed in the 定期購入 tab
   */
  plans?: SubscriptionPlanItem[]

  /**
   * Point packs. Rendered as まとめ買い cards while the tabs are shown, and as
   * plain point cards otherwise.
   */
  pointPacks?: PointPackItem[]

  /**
   * The 不足分 purchase. Rendered as a 都度払い card while the tabs are shown,
   * and as a 不足ポイントのみ購入 card otherwise.
   */
  shortage?: ShortagePurchaseItem | null

  /**
   * Currently selected purchase
   */
  selection?: PointPurchaseSelection

  onSelectionChange?: (selection: PointPurchaseSelection) => void

  /**
   * Expiry date shown in the summary, pre-formatted (e.g. `2019/12/18`)
   */
  expireDate?: string

  /**
   * First payment date shown for subscriptions, pre-formatted (e.g. `7月29日`)
   */
  paymentDate?: string

  /**
   * Day of month for recurring payments, pre-formatted (e.g. `29日`)
   */
  recurringPaymentDay?: string

  /**
   * Card information section and purchase button. Injected by the app so this
   * component stays free of payment concerns.
   */
  cardSection?: React.ReactNode

  subscriptionServiceUrl?: string

  subscriptionSupportUrl?: string
}

/**
 * Point purchase modal.
 *
 * Presentational only — selection and tab state are controlled, and the card
 * form is injected through `cardSection`.
 */
export const PointPurchaseModal = ({
  isLoading,
  showTabs = false,
  tab = 'subscription',
  onTabChange,
  plans = [],
  pointPacks = [],
  shortage,
  selection = null,
  onSelectionChange,
  expireDate,
  paymentDate,
  recurringPaymentDay,
  cardSection,
  subscriptionServiceUrl = DEFAULT_SUBSCRIPTION_SERVICE_URL,
  subscriptionSupportUrl = DEFAULT_SUBSCRIPTION_SUPPORT_URL,
  onClose,
  ...props
}: PointPurchaseModalProps) => {
  // Point cards are only for the states without tabs. Whenever the tabs are
  // shown the spot list is まとめ買い / 都度払い plan cards, shortage or not.
  const usesPointCards = !showTabs

  const activeTab = showTabs ? tab : 'spot'

  const selectedPlan =
    selection?.type === 'subscription'
      ? plans.find((plan) => plan.id === selection.planId)
      : undefined

  const selectedPack =
    selection?.type === 'point'
      ? pointPacks.find((pack) => pack.value === selection.value)
      : undefined

  const isShortageSelected = selection?.type === 'shortage'

  const summaryValue =
    selectedPlan?.purchasePoint ??
    selectedPack?.value ??
    (isShortageSelected ? shortage?.value : undefined)

  // A plan's price is a separate field from the points it grants
  const summaryAmount =
    selectedPlan?.price ??
    selectedPack?.value ??
    (isShortageSelected ? shortage?.value : undefined)

  const summaryExtra =
    selectedPlan?.bonusPoint ?? selectedPack?.returnValue ?? 0

  const summaryFee = isShortageSelected ? shortage?.transactionFee ?? 0 : 0

  // Figma aligns titles across a row: when a card carries the 人気No.1 ribbon,
  // the other card in the same row reserves the ribbon's space as well
  const rowOf = (index: number) => Math.floor(index / SUBSCRIPTION_COLUMNS)
  const labelledRows = new Set(
    plans.map((plan, index) => (plan.isMostPopular ? rowOf(index) : -1)),
  )

  const toggle = (next: NonNullable<PointPurchaseSelection>) => {
    const isSame =
      selection?.type === next.type &&
      (next.type !== 'subscription' ||
        (selection.type === 'subscription' &&
          selection.planId === next.planId)) &&
      (next.type !== 'point' ||
        (selection.type === 'point' && selection.value === next.value))

    onSelectionChange?.(isSame ? null : next)
  }

  return (
    <Modal title="ポイント購入" {...props} onClose={onClose}>
      <Modal.Body
        className={cn(
          'flex justify-center !px-0 pc:w-[500px]',
          // The tab bar sits flush under the header divider
          showTabs && '!pt-0',
        )}
      >
        {isLoading && (
          <div className="flex items-center justify-center pc:min-h-[calc(70vh-118px)]">
            <Loading />
          </div>
        )}

        {!isLoading && (
          <div className="flex w-full flex-col items-center gap-4">
            {/* Full bleed, like the 購入内容 / カード情報 heading bars */}
            {showTabs && (
              <Tabs
                id={tabsId}
                aria-label="購入方法"
                tabs={tabs}
                value={tab}
                onChange={(value) => onTabChange?.(value)}
              />
            )}

            <div className="flex w-full flex-col items-center gap-4 px-4 pc:px-6">
              <div
                className={cn(
                  'w-full',
                  activeTab === 'subscription'
                    ? 'grid grid-cols-2 gap-3'
                    : 'flex flex-col gap-3',
                )}
                {...(showTabs
                  ? {
                      role: 'tabpanel',
                      id: `${tabsId}-panel-${tab}`,
                      'aria-labelledby': `${tabsId}-tab-${tab}`,
                    }
                  : {})}
              >
                {activeTab === 'subscription' &&
                  plans.map((plan, index) => (
                    <PlanCard
                      key={plan.id}
                      type="subscription"
                      state={
                        !plan.isAvailable
                          ? 'unavailable'
                          : selectedPlan?.id === plan.id
                          ? 'selected'
                          : 'default'
                      }
                      label={plan.isMostPopular ? '人気No.1' : undefined}
                      reserveLabelSpace={labelledRows.has(rowOf(index))}
                      description={plan.description}
                      title={plan.name}
                      price={plan.price}
                      priceUnit="/月"
                      point={plan.purchasePoint + plan.bonusPoint}
                      bonusRate={getBonusRate(
                        plan.purchasePoint,
                        plan.bonusPoint,
                      )}
                      unavailableNote={
                        plan.availableAtLabel
                          ? `このプランは${plan.availableAtLabel}以降ご利用いただけます`
                          : undefined
                      }
                      onClick={() =>
                        toggle({ type: 'subscription', planId: plan.id })
                      }
                    />
                  ))}

                {activeTab === 'spot' && (
                  <>
                    {pointPacks.map((pack) =>
                      usesPointCards ? (
                        <PointCard
                          key={pack.value}
                          value={pack.value}
                          selected={selectedPack?.value === pack.value}
                          onClick={() =>
                            toggle({ type: 'point', value: pack.value })
                          }
                        />
                      ) : (
                        <PlanCard
                          key={pack.value}
                          type="bulk"
                          state={
                            selectedPack?.value === pack.value
                              ? 'selected'
                              : 'default'
                          }
                          description={pack.description}
                          title="まとめ買い"
                          price={pack.value}
                          priceUnit="/回"
                          point={pack.value + (pack.returnValue ?? 0)}
                          onClick={() =>
                            toggle({ type: 'point', value: pack.value })
                          }
                        />
                      ),
                    )}

                    {/*
                      Both forms show the shortage broken down into shortage +
                      fee when a fee applies; they differ only in card style.
                    */}
                    {shortage &&
                      (usesPointCards ? (
                        <PointCard
                          title="不足ポイントのみ購入"
                          value={shortage.value}
                          transactionFee={shortage.transactionFee}
                          selected={isShortageSelected}
                          onClick={() => toggle({ type: 'shortage' })}
                        />
                      ) : (
                        <PlanCard
                          type="shortage"
                          state={isShortageSelected ? 'selected' : 'default'}
                          description={shortage.description}
                          title="都度払い"
                          price={shortage.value}
                          priceUnit="/回"
                          point={shortage.value}
                          transactionFee={shortage.transactionFee}
                          onClick={() => toggle({ type: 'shortage' })}
                        />
                      ))}
                  </>
                )}
              </div>

              {showTabs && (
                <a
                  href={subscriptionServiceUrl}
                  target="_blank"
                  className="flex w-full items-center justify-between gap-2 bg-gray-06 px-4 py-3 text-blue-01 typography-body2"
                  rel="noreferrer"
                >
                  定期購入サービスについてはこちら
                  <Arrow className="size-6 shrink-0" />
                </a>
              )}

              <Notes
                variant={showTabs ? 'full' : 'simple'}
                subscriptionSupportUrl={subscriptionSupportUrl}
              />
            </div>

            <PurchaseSummary
              planName={selectedPlan?.name}
              value={summaryValue}
              amount={summaryAmount}
              extra={summaryExtra}
              expireDate={expireDate}
              paymentDate={paymentDate}
              recurringPaymentDay={recurringPaymentDay}
              transactionFee={summaryFee}
            />

            {cardSection}
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}
